import { useMemo } from 'react';

import { CostChangePill, SeeMoreButton } from '@cast/features-cluster-ui';
import { Skeleton, Stack } from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { InventoryListItem } from '../../../_components/ui/InventoryListItem';
import { InventoryWorkloadNameLink } from '../../../_components/ui/InventoryWorkloadNameLink';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { WORKLOAD_CATEGORIES } from '../../../_constants';
import { buildInventoryWorkloadCostItemId } from '../../../_utils/buildInventoryWorkloadCostItemId';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { BIGGEST_COST_MOVERS_SORT } from '../_utils/dashboardListNavigation';
import { useInventoryComputeCostSummaries } from './_hooks/useInventoryComputeCostSummaries';

const MOVERS_LIMIT = 5;

export const BiggestCostMoversCard = () => {
  const {
    workloads,
    isLoading,
    isFetching: hookIsFetching,
    error,
    refetch,
  } = useInventoryComputeCostSummaries();
  const { navigateToComputeList, getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();

  const isFetching = hookIsFetching ?? isLoading;
  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const movers = useMemo(
    () =>
      [...workloads]
        .map((workload) => {
          const change = Number(workload.cost?.totalCostPercentChange || 0);
          return {
            ...workload,
            change,
          };
        })
        .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
        .slice(0, MOVERS_LIMIT),
    [workloads]
  );

  return (
    <WidgetLayout
      title="Biggest cost movers"
      testId="biggest-cost-movers-card"
      headerDivider={false}
      headerEndContent={
        <SeeMoreButton
          onClick={() => navigateToComputeList(BIGGEST_COST_MOVERS_SORT)}
        />
      }
    >
      {isCardLoading ? (
        <Stack flex={1} overflow="auto">
          {Array.from({ length: MOVERS_LIMIT }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              height={24}
              sx={{ my: 6 }}
              data-testid={`biggest-cost-mover-skeleton-${index}`}
            />
          ))}
        </Stack>
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load cost movers"
          refresh={refetch}
          compact
        />
      ) : (
        <Stack flex={1} overflow="auto">
          {movers.map((item, index) => {
            const workloadId = buildInventoryWorkloadCostItemId(item);

            return (
              <InventoryListItem
                key={workloadId}
                testId={`biggest-cost-mover-${item.workloadName}`}
                size="sm"
                divider={index < movers.length - 1}
                slots={{
                  startContent: null,
                  label: (
                    <InventoryWorkloadNameLink
                      to={getWorkloadDashboardPath(
                        workloadId,
                        WORKLOAD_CATEGORIES.COMPUTE
                      )}
                    >
                      {item.workloadName}
                    </InventoryWorkloadNameLink>
                  ),
                  endContent: (
                    <CostChangePill
                      amount={Math.abs(item.change)}
                      isUp={item.change > 0}
                      variant="percent"
                    />
                  ),
                }}
              />
            );
          })}
        </Stack>
      )}
    </WidgetLayout>
  );
};
